/* global ZOHO */
import React, { useEffect, useState, useRef } from 'react';
import './styles.css';
import ZRMLogo from '../src/ZRM_logo.png';

export default function Report() {
  const popupRef = useRef(null);
  const sectionsRef = useRef(null);
  const [results, setResults] = useState({
    Health: {
      score_active: 0,
      score_inactive: 0,
      totalHealth: 0,
    },
  });
  const [showResults, setShowResults] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE;
  const ZOHO_REDIRECT_URI = 'https://healthchecker.zrm.dk/oauth/callback';
  const scope = process.env.REACT_APP_ZOHO_SCOPE;
  const client_id = process.env.REACT_APP_CLIENT_ID;
  const [showApiNames, setShowApiNames] = useState(false);
  const [visibleDuplicates, setVisibleDuplicates] = useState({});
  const [authenticated, setAuthenticated] = useState(false);
  const [creatingReport, setCreatingReport] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [noReport, setNoReport] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  let resultsPolling = false;
  let resultsCompleted = false;
  let interval;

const [loginReady, setLoginReady] = useState(false);

const CONNECTION_NAME = "zohocrm";

 const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const fetchResults = async () => {
  // Prevent duplicate polling loop
  if (resultsCompleted) return; 
  if (resultsPolling) return;
  resultsPolling = true;

  if (!(await checkAuth())) {
    resultsPolling = false;
    console.log("Not logged in");
    return;
  }

  const token = sessionStorage.getItem('apiToken');
  const res = await fetch(API_BASE+'/results', {
    headers: { 'x-api-token': token },
  });

  if (res.status === 401) {
    resultsPolling = false;
    return;
  }

  if (res.status === 202) {
    console.log("Results still loading, retrying...");
    return;
  }

  const data = await res.json();

  if (data.done) {
    resultsCompleted = true;
    resultsPolling = false;
    setResults(data.output);
    setShowResults(true);
    setCreatingReport(false);
    return;
  }

  if (data.started === false) {
    resultsPolling = false;
    setNoReport(true);          // no report yet → show the email form
    return;
  }

  resultsPolling = false;

  setTimeout(fetchResults, 45000);
};

  const startOAuth = async () => {
    const state = "crm";
    const redirectUri = ZOHO_REDIRECT_URI;

    popupRef.current = window.open(
      `https://accounts.zoho.eu/oauth/v2/auth?response_type=code&client_id=${client_id}&scope=${encodeURIComponent(scope)}&redirect_uri=${encodeURIComponent(redirectUri)}&access_type=offline&prompt=consent&state=${encodeURIComponent(state)}`,
      "ZohoOAuth",
      "width=600,height=700"
    );

    return new Promise((resolve, reject) => {
      if (!popupRef.current) {
        reject(new Error("Popup blocked"));
        return;
      }

      const cleanup = () => {
        clearInterval(timer);
        window.removeEventListener("message", handleMessage);
      };

      const timer = setInterval(() => {
        if (popupRef.current.closed) {
          cleanup();
          resolve(false); 
        }
      }, 500);

      const handleMessage = async (event) => {
        const allowedOrigins = [
          window.location.origin,
          'https://healthchecker.zrm.dk',
          'https://healthchecker-30041452713.development.catalystappsail.eu',
          'https://healthchecker-30041452713.catalystappsail.eu/',
          'http://localhost:5000'
        ];
        if (!allowedOrigins.includes(event.origin)) return;

        let data;
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch(e) {
          return;
        }

        if (data?.type === "zoho-auth-success") {
          cleanup();
          sessionStorage.setItem('apiToken', data.token);
          setReportEmail(data.email);
          setAuthenticated(true);
          resolve(true);
        }
      };


      window.addEventListener("message", handleMessage);
    });
  };

const checkAuth = async () => {
  const token = sessionStorage.getItem('apiToken');
  if (!token) return await startOAuth();
  
  const res = await fetch(API_BASE + '/auth/status', {
    headers: { 'x-api-token': token },
  });
  if (!res.ok) return await startOAuth();
  
  const js = await res.json();
  setAuthenticated(js.authenticated);
  return js.authenticated;
};

useEffect(() => {
  const init = async () => {
    const token = sessionStorage.getItem('apiToken');
    if (token) {
      const res = await fetch(API_BASE + '/auth/status', {
        headers: { 'x-api-token': token },
      });
      if (res.ok) {
        const js = await res.json();
        if (js.authenticated) {
          setAuthenticated(true);
          fetchResults();
          return;
        }
      }
    }

    // Try auto-popup — will fail silently if browser blocks it
    try {
      const authed = await startOAuth();
      if (authed) {
        setAuthenticated(true);
        fetchResults();
        return;
      }
    } catch (e) {
      // Popup was blocked — signin card is already showing, user sees the button
      console.log("Popup blocked, showing manual sign in button");
    }

    setAuthenticated(false);
  };

  init();

  // sessionStorage survives a refresh; clear apiToken explicitly so
  // a hard refresh forces re-login instead of reusing the old token.
  const clearTokenOnUnload = () => sessionStorage.removeItem('apiToken');
  window.addEventListener('pagehide', clearTokenOnUnload);

  return () => {
    clearInterval(interval);
    window.removeEventListener('pagehide', clearTokenOnUnload);
  };
}, []);

const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return Number(value).toLocaleString('en-US');
};

const duplicatesByModule  = results?.analysis?.duplicatesByModule;
const totalDuplicates     = results?.analysis?.totalDuplicates;
const noActivityByModule  = results?.analysis?.noActivityByModule;
const totalNoActivity     = results?.analysis?.totalNoActivity;
const emptyFieldsByModule = results?.analysis?.emptyFieldsByModule;
const totalEmptyFields    = results?.analysis?.totalEmptyFields;
const duplicatesImpact    = results?.analysis?.duplicatesImpact;
const overdueCount        = results?.analysis?.overdueCount;
const overdueImpact       = results?.analysis?.overdueImpact;
const noActivityImpact    = results?.analysis?.noActivityImpact;
const emptyImpact         = results?.analysis?.emptyImpact;
const activeImpact        = results?.analysis?.activeImpact;
const inactiveImpact      = results?.analysis?.inactiveImpact;

const today               = formatDate(results?.today); //Datoen som rapporten først blev oprettet
const moduleLabels        = results?.moduleLabels;
const sortByModuleAndName = (a, b) => {
  const moduleA = moduleLabels[a.module?.api_name] || a.module?.api_name || '';
  const moduleB = moduleLabels[b.module?.api_name] || b.module?.api_name || '';
  if (moduleA !== moduleB) {
    return moduleA.localeCompare(moduleB);
  }
  return a.name.localeCompare(b.name);
};
const activeWorkflows = [...(results?.activeWorkflows || [])]
  .sort(sortByModuleAndName);
const activeWorkFlowsPerModule = (results?.activeWorkflowsPerModule || 0);
const inactiveWorkflows = [...(results?.inactiveWorkflows || [])]
  .sort(sortByModuleAndName);
const count_inactiveWorkflows = (results?.analysis?.count_inactiveWorkflows || 0);

const getVisible = (moduleName, type) =>
  visibleDuplicates?.[moduleName]?.[type] ?? 50;

const showMore = (moduleName, type, step = 50) =>
  setVisibleDuplicates((prev) => ({
    ...prev,
    [moduleName]: {
      ...(prev[moduleName] || {}),
      [type]: (prev[moduleName]?.[type] ?? 50) + step,
    },
  }));

const sendToBookingPage = async () => {
  window.open("https://zrm.dk/book/", "_blank");
}

const toggleExpandAll = () => {
  const next = !allExpanded;
  sectionsRef.current?.querySelectorAll('details').forEach((d) => {
    d.open = next;
  });
  setAllExpanded(next);
};

const pollForNewReport = async () => {
  try {
    const res = await fetch(API_BASE + '/results', {
      headers: { 'x-api-token': sessionStorage.getItem('apiToken') },
    });

    if (res.ok) {
      const data = await res.json();
      if (data.done) {
        setResults(data.output);   // old data stayed until exactly here
        setCreatingReport(false);
        return;
      }
    }
  } catch (e) {
    console.error('poll failed:', e);
  }
  setTimeout(pollForNewReport, 45000); // 202 / not-done / error → keep waiting
};

const createNewReport = async () => {
  setCreatingReport(true);
  try {
    const res = await fetch(API_BASE + '/bulk-read', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': sessionStorage.getItem('apiToken'),
      },
      body: JSON.stringify({ email: sessionStorage.getItem('reportEmail') }),
    });
    if (!res.ok) throw new Error('bulk-read failed');
    setTimeout(pollForNewReport, 45000);
  } catch (e) {
    console.error('create new report failed:', e);
    setCreatingReport(false);
  }
};

const generateReport = async () => {
  setCreatingReport(true);
  try {
    const res = await fetch(API_BASE + '/bulk-read', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': sessionStorage.getItem('apiToken'),
      },
      body: JSON.stringify({ email: reportEmail }),
    });
    if (!res.ok) throw new Error('bulk-read failed');
    setNoReport(false);          // /bulk-read set started=true, so polling resumes normally
    setShowResults(false);       // hide the previous report while the new one generates
    resultsCompleted = false;
    resultsPolling = false;
    setTimeout(fetchResults, 45000);
  } catch (e) {
    console.error('generate failed:', e);
    setCreatingReport(false);
  }
};

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const rawHealth = results?.analysis?.rawHealth || 0;
const totalHealth = Number(rawHealth);

let healthClass = 'health-unknown';
if (totalHealth <= 60) healthClass = 'health-bad';
else if (totalHealth >= 80) healthClass = 'health-good';
else healthClass = 'health-warning';

const sections = [
  {
    key: 'duplicates',
    impact: duplicatesImpact,
    show: totalDuplicates > 0,
    render: () => (
        <details className="info-box">
          <summary className="summary-row">
            <h3 className="summary-text">
              The system has {formatNumber(totalDuplicates)} duplicate value
              {totalDuplicates === 1 ? '' : 's'}
            </h3>
            <span className="impact-box">{duplicatesImpact}</span>
          </summary>

          <div className="duplicates-modules">
            {duplicatesByModule.map((m) => (
              <details key={m.moduleName} className="info-box">
                <summary className="summary-row">
                  <span>
                    {m.label}
                    {showApiNames ? ` (${m.moduleName}) ` : ' '} 
                    has <strong>{formatNumber(m.total)}</strong> duplicate
                    {m.total === 1 ? '' : 's'}
                  </span>
                </summary>

                <div className="duplicates-types">
                  {(m.apiDuplicates?.length ?? 0) > 0 && (
                    <details>
                      <summary className="duplicate-type-summary">
                        There {m.apiDuplicates.length === 1 ? 'is' : 'are'}{' '}
                        {m.apiDuplicates.length} duplicate{' '}
                        {m.moduleName === 'Accounts' ? 'account name' : 'email'}
                        {m.apiDuplicates.length === 1 ? '' : 's'}
                      </summary>

                      <ul className="duplicates-grid">
                        {m.apiDuplicates
                          .slice(0, getVisible(m.moduleName, 'api'))
                          .map((dup, idx) => (
                            <li key={idx}>
                              <a href={`https://crm.zoho.eu/crm/GlobalSearch1.do?sModules=${encodeURIComponent(m.moduleName)}&searchword=${encodeURIComponent(dup)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="duplicate-link"
                                onClick={(e) => e.stopPropagation()}>
                                {dup}
                              </a>
                            </li>
                          ))}
                      </ul>

                      {getVisible(m.moduleName, 'api') < m.apiDuplicates.length && (
                        <button
                          type="button"
                          className="button-primary"
                          onClick={() => showMore(m.moduleName, 'api')}
                        >
                          Show more
                        </button>
                      )}
                    </details>
                  )}

                  {(m.phoneDuplicates?.length ?? 0) > 0 && (
                    <details>
                      <summary className="duplicate-type-summary">
                        There {m.phoneDuplicates.length === 1 ? 'is' : 'are'}{' '}
                        {m.phoneDuplicates.length} duplicate phone
                        {m.phoneDuplicates.length === 1 ? '' : 's'}
                      </summary>

                      <ul className="duplicates-grid">
                        {m.phoneDuplicates
                          .slice(0, getVisible(m.moduleName, 'phones'))
                          .map((dup, idx) => (
                            <li key={idx}>
                              <a href={`https://crm.zoho.eu/crm/GlobalSearch1.do?sModules=${encodeURIComponent(m.moduleName)}&searchword=${encodeURIComponent(dup)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="duplicate-link"
                                onClick={(e) => e.stopPropagation()}>
                                {dup}
                              </a>
                            </li>
                          ))}
                      </ul>

                      {getVisible(m.moduleName, 'phones') < m.phoneDuplicates.length && (
                        <button
                          type="button"
                          className="button-primary"
                          onClick={() => showMore(m.moduleName, 'phones')}
                        >
                          Show more
                        </button>
                      )}
                    </details>
                  )}
                </div>
              </details>
            ))}
          </div>
      </details>
    )
  },
  {
    key: 'tasks',
    impact: overdueImpact,
    show: (overdueCount ?? 0) > 0,
    render: () => (
      <details className="info-box no-more-info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {formatNumber(overdueCount)} overdue task
            {overdueCount === 1 ? '' : 's'}
          </h3>
          <span className="impact-box">{overdueImpact}</span>
        </summary>
      </details>
    )
  },
  {
    key: 'noActivity',
    impact: noActivityImpact,
    show: totalNoActivity > 0,
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {formatNumber(totalNoActivity)} record
            {totalNoActivity === 1 ? '' : 's'} without activity
          </h3>
          <span className="impact-box">{noActivityImpact}</span>
        </summary>

        <div className="duplicates-modules">
          {noActivityByModule.map((m) => (
                <div className="summary-row">
                  {m.label}
                  {showApiNames ? ` (${m.moduleName})` : ''} has{' '}
                  <strong>{formatNumber(m.count)}</strong> record
                  {m.count === 1 ? '' : 's'} without activity in the past
                  {m.moduleName === 'Leads' ? ' one month' : ' three months'}
                </div>
          ))}
        </div>
      </details>
    )
  },

  {
    key: 'empty',
    impact: emptyImpact,
    show: totalEmptyFields > 0,
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {formatNumber(totalEmptyFields)} empty field
            {totalEmptyFields === 1 ? '' : 's'}
          </h3>
          <span className="impact-box">{emptyImpact}</span>
        </summary>
          <div>
          {emptyFieldsByModule.map((m) => (
            <details key={m.moduleName} className="info-box">
              <summary>
                <div className="summary-row">
                  <span>
                    {m.label}
                    {showApiNames ? ` (${m.moduleName})` : ''}
                    {' '}has <strong>{formatNumber(m.count)}</strong> empty field
                    {m.count === 1 ? '' : 's'}
                  </span>
                </div>
              </summary>
              <div className="percent_div duplicates-grid">
                {m.fields.length > 0 &&
                [...m.fields]
                  .sort((a, b) => a.label.localeCompare(b.label, 'da'))
                  .map((f) => (
                    <div key={f.key} className="field_div">
                      <div className="field-name_div">
                        <span>{f.label}</span>
                        {showApiNames ? (
                          <span className="api-text"> ({f.api})</span>
                        ) : null}
                      </div>
                      <div className="field-percent_div">{f.emptyPercent}%</div>
                    </div>
                  ))}
              </div>
            </details>
          ))}
          </div>
      </details>
    )
  },

  {
    key: 'activeWorkflows',
    impact: activeImpact,
    show: (activeWorkFlowsPerModule < 5),
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system only has {formatNumber(activeWorkFlowsPerModule)} active workflow
            {activeWorkFlowsPerModule === 1 ? ' ' : 's '}
            per module
          </h3>
          <span className="impact-box">{activeImpact}</span>
        </summary>
          <div>
              <div className="workflow_div">
              <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                        <th>Name</th>
                        <th>Module</th>
                        {showApiNames && (
                          <th>API</th>
                        )}
                        <th>Created By</th>
                        <th>Last Modified</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(activeWorkflows ?? []).map((wf) => (
                        <tr key={wf.id} className="hover:bg-gray-50">
                        <td>{wf.name || '—'}</td>
                        <td>{moduleLabels?.[wf.module?.api_name] || wf.module?.api_name || '—'}</td>
                        {showApiNames && (
                        <td>{wf.module?.api_name || '—'}</td>
                        )}
                        <td>{wf.created_by?.name || '—'}</td>
                        <td>{wf.modified_time ? formatDate(wf.modified_time) : '—'}</td>
                        </tr>
                    ))}
                    </tbody>
              </table>
              </div>
          </div>
        </details>
    )
  },

  {
    key: 'inactiveWorkflows',
    impact: inactiveImpact,
    show: (count_inactiveWorkflows > 0),
    render: () => (
      <details className="info-box">
        <summary className="summary-row">
          <h3 className="summary-text">
            The system has {count_inactiveWorkflows} inactive workflow
            {inactiveWorkflows?.length === 1 ? '' : 's'}
          </h3>
          <span className="impact-box">{inactiveImpact}</span>
        </summary>
        <div>
            <div>
                <div className="workflow_div">
                <table className="min-w-full text-sm text-left border border-gray-200">
                    <thead className="bg-gray-100">
                    <tr>
                        <th>Name</th>
                        <th>Module</th>
                        {showApiNames && (
                          <th>API</th>
                        )}
                        <th>Created By</th>
                        <th>Last Modified</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(inactiveWorkflows ?? []).map((wf) => (
                        <tr key={wf.id} className="hover:bg-gray-50">
                        <td>{wf.name || '—'}</td>
                        <td>{moduleLabels?.[wf.module?.api_name] || wf.module?.api_name || '—'}</td>
                        {showApiNames && (
                        <td>{wf.module?.api_name || '—'}</td>
                        )}
                        <td>{wf.created_by?.name || '—'}</td>
                        <td>{wf.modified_time ? formatDate(wf.modified_time) : '—'}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
        </details>
    )
  }
];

  return (
    <div className="landing">
                <a href="https://zrm.dk">
      <img fetchpriority="high" width="100" src="https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1.png" className="attachment-full size-full wp-image-5631" alt="ZRM logo mørk" srcSet="https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1.png 6329w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-300x89.png 300w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-1024x305.png 1024w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-768x229.png 768w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-1536x457.png 1536w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-2048x610.png 2048w, https://zrm.dk/wp-content/uploads/2024/11/Logo_dark-e1727709595774-1-1568x467.png 1568w" sizes="(max-width: 6329px) 100vw, 6329px" />
        </a>
          <div className="topBanner">
            <h1> Health Checker</h1>
          </div>
      <div className="report-header">
        {!authenticated && (
        <div class="arrow" >
        </div>
        )}
        {!authenticated && (
        <div className="signin-card">
          <h2>Sign in to view your report</h2>
          <p>
            This page will open a sign-in popup. If nothing happens,{' '}
            <strong>allow popups</strong> for this site in your browser and try the button below.
          </p>
          <button
            className="cta-button"
            onClick={async () => {
              const authed = await startOAuth();
              if (authed) {
                setAuthenticated(true);
                fetchResults();
              }
            }}
          >
            Sign in with Zoho
          </button>
        </div>
      )}
        {authenticated && noReport && (
          <div className="report-email-section">
            <p>The report will be sent to:</p>
            <div className="report-generate-section">
              <input
                type="email"
                placeholder="Enter email to send report to"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value.trim().toLowerCase())}
                className="report-email-input"
              />
              <button
                className="cta-button"
                disabled={!isValidEmail(reportEmail)}
                title={!isValidEmail(reportEmail) ? "Enter a valid email address first" : ""}
                onClick={generateReport}
              >
                {isValidEmail(reportEmail) ? "Generate New Report" : "Enter email first"}
              </button>
            </div>
            {reportEmail.length > 0 && !isValidEmail(reportEmail) && (
              <div className="error-stext">Please enter a valid email address</div>
            )}
            <section className="landing-features">
            <ul>
              <li>Duplicate records across Accounts, Contacts and Leads</li>
              <li>Count of overdue Tasks</li>
              <li>Empty records and missing activity</li>
              <li>Workflow insights</li>
            </ul>
          </section>
          </div>          
        )}

        {authenticated && creatingReport && (
          <div className="signin-card">
            <h2>Report Generating</h2>
          </div>
        )}

        {authenticated && !noReport && !creatingReport && (!showResults || !results) && (
          <h2>Report is still generating. You will receive an email when it is done</h2>
        )}
      </div>
      <div>
        <div>
        {showResults && results && !creatingReport && (
          <div ref={sectionsRef}>
            <div className={`health-summary ${healthClass}`}>
              <div className="health-label">Health Score</div>
              <div className="health-value">
                {typeof totalHealth === 'number' ? `${totalHealth.toFixed(1)}%` : '—'}
              </div>
            </div>
          {(healthClass === 'health-bad') && (
            <div className='booking-div'>
              <button
                className="cta-button"
                onClick={sendToBookingPage}>
                Book free it-consultation
              </button>
            </div>
          )}

          <div className="report-header-card">
            <div className="report-header-meta">
              <span className="report-date-label">Report generated</span>
              <span className="report-date-value">{today}</span>
            </div>

            <div className="report-new-section">
              <div className="report-new-label">
                <span className="report-new-title">Generate a new report</span>
                <span className="report-new-sub">A new report will be run and sent to the email below</span>
              </div>
              <div className="report-header-actions">
                {creatingReport ? (
                  <span className="generation-note">Generating…</span>
                ) : (
                  <>
                    <input
                      type="email"
                      placeholder="Email to send report to"
                      value={reportEmail}
                      onChange={(e) => setReportEmail(e.target.value.trim().toLowerCase())}
                      className="report-email-input"
                    />
                    <button
                      className="cta-button"
                      disabled={!isValidEmail(reportEmail)}
                      title={!isValidEmail(reportEmail) ? "Enter a valid email address first" : ""}
                      onClick={generateReport}
                    >
                      {isValidEmail(reportEmail) ? "Generate New Report" : "Enter email first"}
                    </button>
                  </>
                )}
              </div>
              {reportEmail.length > 0 && !isValidEmail(reportEmail) && (
                <div className="error-stext">Please enter a valid email address</div>
              )}
            </div>
          </div>

          {results?.analysis && (
            <div className="analysis-container">
              <h3>{results?.analysis?.introduction}</h3>
              
              <ul>
                {results?.analysis?.points.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>

              <p>{results?.analysis?.ending}</p>
            </div>
          )}
            <button
                type="button"
                className="button-primary"
                onClick={() => setShowApiNames((v) => !v)}
                >
                {showApiNames ? 'Hide API names' : 'Show API names'}
            </button>
            <button
                type="button"
                className="button-primary"
                onClick={toggleExpandAll}>
                {allExpanded ? 'Close All' : 'Expand All'}
            </button>
            {sections
              .filter((s) => s.show)
              .sort((a, b) => {
                // Håndterer impact som både procentstreng og tal
                const parseImpact = (impact) => {
                  if (typeof impact === 'string') {
                    const num = parseFloat(impact.replace('%', ''));
                    return isNaN(num) ? 0 : num; // Hvis værdien ikke er et tal, returner 0
                  }
                  return impact; // Hvis det allerede er et tal, returner det direkte
                };

                const aImpact = parseImpact(a.impact);
                const bImpact = parseImpact(b.impact);

                return aImpact - bImpact; // Sorter mest negativ først
              })
              .map((s) => (
                <React.Fragment key={s.key}>
                  {s.render()}
                </React.Fragment>
              ))}

        </div>
        )}
      </div>
    </div>
    {/* Fixed Footer */}
    <div className="footer">
      <p>Powered by </p>
      <img
        src={ZRMLogo}
        alt="Powered by ZRM"
        style={{
          maxWidth: "50px",
          height: "auto",
          marginRight: "10px",
          verticalAlign: "middle",
        }}
      />
    </div>
      </div>
  );
}
